/**
 * 导航上下文管理
 * 职责：管理页面导航上下文，解决SPA应用referrer和source_page问题
 */

import { contextManager } from "./ContextManager";

export interface NavigationContextData {
  // 当前页面
  current_url: string;
  current_page_nm: string;
  current_sub_page_nm?: string;

  // 来源页面
  referrer: string;
  source_page: string;

  // 导航历史（最近 10 个页面）
  navigation_history: Array<{
    url: string;
    page_nm: string;
    timestamp: number;
  }>;

  // 会话信息
  session_id: string;
  session_start_url: string; // 会话首个页面
  session_start_referrer: string; // 会话的初始 referrer

  // 元数据
  _createdAt: number;
  _source: "initial" | "spa_navigation" | "page_refresh" | "new_tab";
}

const NAVIGATION_CONTEXT_KEY = "navigation";
const NAVIGATION_CONTEXT_TTL = 0; // 0 表示永不过期，直到标签页关闭

export class NavigationContext {
  private static storageKey = "_tracker_navigation_context";

  /**
   * 初始化导航上下文
   */
  static init(): void {
    // 1. 尝试从 sessionStorage 恢复
    const saved = this.loadFromStorage();
    if (saved) {
      contextManager.register(NAVIGATION_CONTEXT_KEY, {
        type: "page",
        data: saved,
        metadata: {
          createdAt: Date.now(),
          ttl: NAVIGATION_CONTEXT_TTL,
          scope: "global",
          sessionId: saved.session_id,
        },
      });
      this.updateSource("page_refresh");

      if (process.env.NODE_ENV === "development") {
        console.log("[NavigationContext] Restored from storage:", saved);
      }
      return;
    }

    // 2. 创建新的上下文
    const data: NavigationContextData = {
      current_url: typeof window !== "undefined" ? window.location.href : "",
      current_page_nm: "",
      referrer: typeof document !== "undefined" ? document.referrer || "" : "",
      source_page:
        typeof document !== "undefined" ? document.referrer || "" : "",
      navigation_history: [],
      session_id: this.generateSessionId(),
      session_start_url:
        typeof window !== "undefined" ? window.location.href : "",
      session_start_referrer:
        typeof document !== "undefined" ? document.referrer || "" : "",
      _createdAt: Date.now(),
      _source:
        typeof document !== "undefined" && document.referrer
          ? "initial"
          : "new_tab",
    };

    contextManager.register(NAVIGATION_CONTEXT_KEY, {
      type: "page",
      data,
      metadata: {
        createdAt: Date.now(),
        ttl: NAVIGATION_CONTEXT_TTL,
        scope: "global",
        sessionId: data.session_id,
      },
    });

    this.saveToStorage();

    if (process.env.NODE_ENV === "development") {
      console.log("[NavigationContext] Initialized:", data);
    }
  }

  /**
   * 页面切换时更新上下文
   */
  static onPageChange(
    newUrl: string,
    pageName: string,
    subPageName?: string
  ): void {
    const current = this.get();
    if (!current) {
      this.init();
      return;
    }

    // 记录更新前的状态
    const oldPageName = current.current_page_nm;
    const oldSubPageName = current.current_sub_page_nm;
    const oldUrl = current.current_url;

    // 保存当前页面到历史记录
    if (current.current_page_nm) {
      current.navigation_history.push({
        url: current.current_url,
        page_nm: current.current_page_nm,
        timestamp: Date.now(),
      });

      // 限制历史记录长度
      if (current.navigation_history.length > 10) {
        current.navigation_history.shift();
      }
    }

    // 更新 referrer 和 source_page
    current.referrer = current.current_url;
    current.source_page = current.current_url;

    // 更新当前页面
    current.current_url = newUrl;
    current.current_page_nm = pageName;
    current.current_sub_page_nm = subPageName;

    // 更新来源标记
    current._source = "spa_navigation";

    // 更新上下文
    contextManager.register(NAVIGATION_CONTEXT_KEY, {
      type: "page",
      data: current,
      metadata: {
        createdAt: Date.now(),
        ttl: NAVIGATION_CONTEXT_TTL,
        scope: "global",
        sessionId: current.session_id,
      },
    });

    this.saveToStorage();

    if (process.env.NODE_ENV === "development") {
      console.log("[NavigationContext] Updated:", {
        from: {
          url: oldUrl,
          page_nm: oldPageName,
          sub_page_nm: oldSubPageName,
        },
        to: {
          url: newUrl,
          page_nm: pageName,
          sub_page_nm: subPageName,
        },
        navigation_path: this.getNavigationPath(),
      });
    }
  }

  /**
   * 获取导航上下文
   */
  static get(): NavigationContextData | null {
    const context = contextManager.get(NAVIGATION_CONTEXT_KEY);
    return context?.data as NavigationContextData | null;
  }

  /**
   * 获取 referrer（兼容旧 API）
   */
  static getReferrer(): string {
    const data = this.get();
    return (
      data?.referrer ||
      (typeof document !== "undefined" ? document.referrer || "" : "")
    );
  }

  /**
   * 获取 source_page（兼容旧 API）
   */
  static getSourcePage(): string {
    const data = this.get();
    return (
      data?.source_page ||
      (typeof document !== "undefined" ? document.referrer || "" : "")
    );
  }

  /**
   * 获取导航路径（用于数据分析）
   */
  static getNavigationPath(): string[] {
    const data = this.get();
    if (!data) return [];

    return [
      ...data.navigation_history.map((h) => h.page_nm),
      data.current_page_nm,
    ].filter(Boolean);
  }

  /**
   * 清除上下文（用于测试）
   */
  static clear(): void {
    contextManager.clear(NAVIGATION_CONTEXT_KEY);
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(this.storageKey);
      }
    } catch (e) {
      // sessionStorage 不可用时降级
    }

    if (process.env.NODE_ENV === "development") {
      console.log("[NavigationContext] Cleared");
    }
  }

  /**
   * 从 sessionStorage 加载
   */
  private static loadFromStorage(): NavigationContextData | null {
    try {
      if (typeof sessionStorage === "undefined") return null;

      const saved = sessionStorage.getItem(this.storageKey);
      if (!saved) return null;

      return JSON.parse(saved);
    } catch (e) {
      return null;
    }
  }

  /**
   * 保存到 sessionStorage
   */
  private static saveToStorage(): void {
    const data = this.get();
    if (!data) return;

    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      }
    } catch (e) {
      // sessionStorage 不可用时降级
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[NavigationContext] Failed to save to sessionStorage:",
          e
        );
      }
    }
  }

  /**
   * 生成会话 ID
   */
  private static generateSessionId(): string {
    return `nav_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  /**
   * 更新来源标记
   */
  private static updateSource(source: NavigationContextData["_source"]): void {
    const data = this.get();
    if (data) {
      data._source = source;
      contextManager.register(NAVIGATION_CONTEXT_KEY, {
        type: "page",
        data,
        metadata: {
          createdAt: Date.now(),
          ttl: NAVIGATION_CONTEXT_TTL,
          scope: "global",
          sessionId: data.session_id,
        },
      });
      this.saveToStorage();
    }
  }

  /**
   * 订阅导航上下文变化
   */
  static subscribe(
    listener: (data: NavigationContextData | null) => void
  ): () => void {
    return contextManager.subscribe(NAVIGATION_CONTEXT_KEY, (context) => {
      listener(context?.data as NavigationContextData | null);
    });
  }
}
